/**
 * D244, D358: the game's sounds, made in the browser with the Web Audio API. No audio files.
 *
 * The audio context is made on the first tap or key press, because iOS Safari will not
 * start one any other way. Each sound has a shortest gap between plays, so fast tapping
 * does not stack voices and clip. Every setting is per device, in localStorage, and sound
 * is on until the player turns it off.
 *
 * D358: a small mixer. Master is the one-tap-away switch (`isMuted`/`setMuted`, unchanged
 * since D244) plus its own volume; below it, every sound belongs to one of four channels
 * (taps and buttons, rewards, menus, map ambience), each with its own volume and off
 * switch. A play is heard only when master and its channel are both on, scaled by both
 * volumes.
 */
import { useEffect, useState } from 'preact/hooks';

export type SoundName =
  'tap' | 'coin' | 'buy' | 'milestone' | 'hire' | 'welcome' | 'festival' | 'error';

/** D358: the four channels below master. */
export type SoundChannel = 'taps' | 'rewards' | 'menus' | 'ambience';

export const SOUND_CHANNELS: SoundChannel[] = ['taps', 'rewards', 'menus', 'ambience'];

/** The channel's own words, for the Sound settings page. */
export const CHANNEL_LABEL: Record<SoundChannel, string> = {
  taps: 'Taps and buttons',
  rewards: 'Rewards',
  menus: 'Menus',
  ambience: 'Map ambience',
};

/** Which channel each sound belongs to. */
export const SOUND_CHANNEL: Record<SoundName, SoundChannel> = {
  tap: 'taps',
  buy: 'taps',
  error: 'taps',
  coin: 'rewards',
  milestone: 'rewards',
  hire: 'rewards',
  festival: 'rewards',
  welcome: 'menus',
};

export interface PlayOptions {
  /** How many taps in a row. The coin rises a semitone a step, up to an octave. */
  combo?: number;
}

/** The master volume, out of 1. Kept low: these sit under the game, not over it. */
export const MASTER_VOLUME = 0.18;

/** The shortest gap between two plays of the same sound, in milliseconds. */
export const MIN_GAP_MS: Record<SoundName, number> = {
  tap: 45,
  coin: 60,
  buy: 80,
  milestone: 400,
  hire: 300,
  welcome: 1000,
  festival: 1500,
  error: 150,
};

export const MUTE_KEY = 'hedgerow-sound-muted';

// ---------------------------------------------------------------------------------------
// Rate limiting. Pure: the caller passes the time.

export interface RateLimiter {
  /** True, and the play is counted, when `key` has been quiet for its gap at `now`. */
  allow(key: string, now: number): boolean;
}

export function createRateLimiter(gapFor: (key: string) => number): RateLimiter {
  const last = new Map<string, number>();
  return {
    allow(key, now) {
      const prev = last.get(key);
      if (prev !== undefined && now - prev < gapFor(key)) return false;
      last.set(key, now);
      return true;
    },
  };
}

// ---------------------------------------------------------------------------------------
// Mute, per device.

/** The stored switch. Sound is on unless the device says otherwise or storage is shut. */
export function readMuted(): boolean {
  try {
    return globalThis.localStorage?.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

let muted = readMuted();
const listeners = new Set<(muted: boolean) => void>();

export function isMuted(): boolean {
  return muted;
}

/** Turn sound off or on. Kept for this session even when storage cannot be written. */
export function setMuted(value: boolean): void {
  muted = value;
  try {
    if (value) globalThis.localStorage?.setItem(MUTE_KEY, '1');
    else globalThis.localStorage?.removeItem(MUTE_KEY);
  } catch {
    // A private window: the switch holds until the page closes.
  }
  for (const fn of listeners) fn(value);
}

/** Read the stored switch again, as at start up. For the tests. */
export function reloadMuted(): boolean {
  muted = readMuted();
  return muted;
}

/** Listen for the switch. Returns the way to stop. */
export function onMutedChange(fn: (muted: boolean) => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

/** The mute switch for a settings row: `[muted, setMuted]`. */
export function useSoundMuted(): [boolean, (muted: boolean) => void] {
  const [value, setValue] = useState(muted);
  useEffect(() => onMutedChange(setValue), []);
  return [value, setMuted];
}

// ---------------------------------------------------------------------------------------
// D358: the mixer. Master's own volume, and each channel's volume and off switch, all per
// device. A missing or unreadable value reads as the slider's default (full, and on).

const VOLUME_KEY = (channel: 'master' | SoundChannel) => `hedgerow-sound-vol-${channel}`;
const OFF_KEY = (channel: SoundChannel) => `hedgerow-sound-off-${channel}`;

function readVolume(key: string): number {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (raw === null || raw === undefined) return 1;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1;
  } catch {
    return 1;
  }
}

function writeVolume(key: string, value: number): void {
  try {
    globalThis.localStorage?.setItem(key, String(Math.min(1, Math.max(0, value))));
  } catch {
    // Kept for this session only.
  }
}

function readOff(key: string): boolean {
  try {
    return globalThis.localStorage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeOff(key: string, value: boolean): void {
  try {
    if (value) globalThis.localStorage?.setItem(key, '1');
    else globalThis.localStorage?.removeItem(key);
  } catch {
    // Kept for this session only.
  }
}

let masterVolume = readVolume(VOLUME_KEY('master'));
const masterVolumeListeners = new Set<(v: number) => void>();

/** Master's own volume, out of 1, on top of the one-tap-away switch. */
export function readMasterVolume(): number {
  return masterVolume;
}

export function setMasterVolume(value: number): void {
  masterVolume = Math.min(1, Math.max(0, value));
  writeVolume(VOLUME_KEY('master'), masterVolume);
  for (const fn of masterVolumeListeners) fn(masterVolume);
}

/** Read the stored master volume again. For the tests. */
export function reloadMasterVolume(): number {
  masterVolume = readVolume(VOLUME_KEY('master'));
  return masterVolume;
}

export function useMasterVolume(): [number, (v: number) => void] {
  const [value, setValue] = useState(masterVolume);
  useEffect(() => {
    masterVolumeListeners.add(setValue);
    return () => void masterVolumeListeners.delete(setValue);
  }, []);
  return [value, setMasterVolume];
}

const channelVolumes: Record<SoundChannel, number> = {
  taps: readVolume(VOLUME_KEY('taps')),
  rewards: readVolume(VOLUME_KEY('rewards')),
  menus: readVolume(VOLUME_KEY('menus')),
  ambience: readVolume(VOLUME_KEY('ambience')),
};
const channelOff: Record<SoundChannel, boolean> = {
  taps: readOff(OFF_KEY('taps')),
  rewards: readOff(OFF_KEY('rewards')),
  menus: readOff(OFF_KEY('menus')),
  ambience: readOff(OFF_KEY('ambience')),
};
const channelListeners = new Set<(channel: SoundChannel) => void>();

export function readChannelVolume(channel: SoundChannel): number {
  return channelVolumes[channel];
}

export function setChannelVolume(channel: SoundChannel, value: number): void {
  channelVolumes[channel] = Math.min(1, Math.max(0, value));
  writeVolume(VOLUME_KEY(channel), channelVolumes[channel]);
  for (const fn of channelListeners) fn(channel);
}

export function readChannelOff(channel: SoundChannel): boolean {
  return channelOff[channel];
}

export function setChannelOff(channel: SoundChannel, value: boolean): void {
  channelOff[channel] = value;
  writeOff(OFF_KEY(channel), value);
  for (const fn of channelListeners) fn(channel);
}

/** Read every channel's stored settings again. For the tests. */
export function reloadChannelSettings(): void {
  for (const c of SOUND_CHANNELS) {
    channelVolumes[c] = readVolume(VOLUME_KEY(c));
    channelOff[c] = readOff(OFF_KEY(c));
  }
}

/**
 * One channel's row for the Sound settings page: its volume, its off switch, and the way to
 * change each. Redraws only while that channel's own values change.
 */
export function useSoundChannel(
  channel: SoundChannel,
): [
  { volume: number; off: boolean },
  { setVolume: (v: number) => void; setOff: (v: boolean) => void },
] {
  const [value, setValue] = useState({ volume: channelVolumes[channel], off: channelOff[channel] });
  useEffect(() => {
    const fn = (c: SoundChannel) => {
      if (c === channel) setValue({ volume: channelVolumes[channel], off: channelOff[channel] });
    };
    channelListeners.add(fn);
    return () => void channelListeners.delete(fn);
  }, [channel]);
  return [
    value,
    {
      setVolume: (v: number) => setChannelVolume(channel, v),
      setOff: (v: boolean) => setChannelOff(channel, v),
    },
  ];
}

// ---------------------------------------------------------------------------------------
// The audio context.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let unlockInstalled = false;
const limiter = createRateLimiter((k) => MIN_GAP_MS[k as SoundName] ?? 50);

function audioCtor(): typeof AudioContext | null {
  const g = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: unknown };
  return g.AudioContext ?? (g.webkitAudioContext as typeof AudioContext | undefined) ?? null;
}

/** Make or wake the context. Call from inside a user gesture. */
export function unlockAudio(): void {
  try {
    if (!ctx) {
      const Ctor = audioCtor();
      if (!Ctor) return;
      ctx = new Ctor();
      const comp = ctx.createDynamicsCompressor();
      master = ctx.createGain();
      // D358: the fixed baseline; master and channel volume are applied per play instead,
      // since each sound may belong to a different channel.
      master.gain.value = MASTER_VOLUME;
      master.connect(comp);
      comp.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    ctx = null;
    master = null;
  }
}

/**
 * Wake the audio on the first tap or key press anywhere. Call once at start up; safe to call
 * again. The listeners stay, so a context the browser suspends wakes on the next tap.
 */
export function installAudioUnlock(): void {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const opts = { capture: true, passive: true } as const;
  for (const ev of ['pointerdown', 'touchend', 'keydown'])
    window.addEventListener(ev, unlockAudio, opts);
}

// ---------------------------------------------------------------------------------------
// The voices.

export interface Tone {
  freq: number;
  /** Seconds after the sound starts. */
  at?: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  /** Slide to this frequency over the tone. */
  to?: number;
}

function tone(c: AudioContext, out: AudioNode, t0: number, t: Tone, volume = 1): void {
  const start = t0 + (t.at ?? 0);
  const end = start + t.dur;
  const osc = c.createOscillator();
  const env = c.createGain();
  osc.type = t.type ?? 'sine';
  osc.frequency.setValueAtTime(t.freq, start);
  if (t.to) osc.frequency.exponentialRampToValueAtTime(t.to, end);
  const peak = (t.gain ?? 0.5) * volume;
  const attack = t.attack ?? 0.005;
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(peak, start + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, end);
  osc.connect(env);
  env.connect(out);
  osc.start(start);
  osc.stop(end + 0.02);
}

/** A note's frequency, in semitones from A4. */
const note = (semitones: number) => 440 * 2 ** (semitones / 12);
const C4 = note(-9);
const E4 = note(-5);
const G4 = note(-2);
const C5 = note(3);
const E5 = note(7);
const G5 = note(10);
const C6 = note(15);
const E6 = note(19);

/** The tones for each sound. Exported for the tests; not part of the screens' API. */
export function voices(name: SoundName, combo = 0): Tone[] {
  switch (name) {
    case 'tap':
      return [
        { freq: 520, to: 380, dur: 0.14, type: 'triangle', gain: 0.5 },
        { freq: 1040, dur: 0.05, gain: 0.12 },
      ];
    case 'coin': {
      const lift = 2 ** (Math.min(Math.max(0, combo), 12) / 12);
      return [
        { freq: note(14) * lift, dur: 0.09, gain: 0.3 },
        { freq: note(21) * lift, at: 0.06, dur: 0.22, gain: 0.3 },
      ];
    }
    case 'buy':
      return [{ freq: 280, to: 620, dur: 0.09, gain: 0.55 }];
    case 'milestone':
      return [C5, E5, G5, C6].map((freq, i) => ({
        freq,
        at: i * 0.075,
        dur: i === 3 ? 0.35 : 0.14,
        type: 'triangle' as const,
        gain: 0.35,
      }));
    case 'hire':
      return [
        { freq: G5, dur: 0.12, type: 'triangle', gain: 0.38 },
        { freq: C6, at: 0.11, dur: 0.24, type: 'triangle', gain: 0.38 },
      ];
    case 'welcome':
      return [C4, E4, G4, C5].map((freq, i) => ({
        freq,
        at: i * 0.03,
        dur: 0.9,
        attack: 0.06,
        gain: 0.2,
      }));
    case 'festival':
      return [
        { freq: G4, dur: 0.12, type: 'triangle', gain: 0.35 },
        { freq: C5, at: 0.12, dur: 0.12, type: 'triangle', gain: 0.35 },
        { freq: E5, at: 0.24, dur: 0.12, type: 'triangle', gain: 0.35 },
        { freq: G5, at: 0.36, dur: 0.7, type: 'triangle', gain: 0.3 },
        { freq: C6, at: 0.36, dur: 0.7, gain: 0.18 },
        { freq: E6, at: 0.36, dur: 0.7, gain: 0.12 },
      ];
    case 'error':
      return [{ freq: 150, to: 95, dur: 0.12, gain: 0.18 }];
  }
}

/**
 * Play a sound. Quiet when muted, its channel is off, either volume is down, before the
 * first tap, where there is no Web Audio, and when the same sound played too recently.
 */
export function playSound(name: SoundName, opts: PlayOptions = {}): void {
  if (muted || !ctx || !master || ctx.state !== 'running') return;
  const channel = SOUND_CHANNEL[name];
  if (channelOff[channel]) return;
  const volume = masterVolume * channelVolumes[channel];
  if (volume <= 0) return;
  if (!limiter.allow(name, ctx.currentTime * 1000)) return;
  try {
    const t0 = ctx.currentTime + 0.005;
    for (const t of voices(name, opts.combo)) tone(ctx, master, t0, t, volume);
  } catch {
    // A sound is never worth an error.
  }
}
